import React, { useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import WarningIcon from '@material-ui/icons/Warning'
import SyncIcon from '@material-ui/icons/Sync'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import { useStyles } from './styles'
import { useCheckArCodesAvailable } from '../../hooks/useMoveOutManagement/useMoveOutManagement'

interface FinancialConnectionDialogProps {
    open: boolean
    onClose: () => void
}

export const FinancialConnectionDialog: React.FC<FinancialConnectionDialogProps> = ({
    open,
    onClose,
}) => {
    const classes = useStyles()
    const [connectionResult, setConnectionResult] = useState<
        'none' | 'success' | 'failed'
    >('none')

    const {
        mutate: checkConnection,
        isLoading: checking,
    } = useCheckArCodesAvailable()

    const handleCheckConnection = () => {
        setConnectionResult('none')
        checkConnection(undefined, {
            onSuccess: (data) => {
                setConnectionResult(
                    data.entrata_ar_codes_available ? 'success' : 'failed',
                )
            },
            onError: () => {
                setConnectionResult('failed')
            },
        })
    }

    const handleClose = () => {
        setConnectionResult('none')
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.configDialogPaper }}
        >
            <DialogTitle
                disableTypography
                className={classes.configDialogTitle}
            >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <WarningIcon style={{ color: '#f59e0b' }} />
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Financial Data Not Available
                    </Typography>
                </Box>
                <IconButton size="small" onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers className={classes.configDialogContent}>
                <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
                    <Typography variant="body1">
                        Deposit, balance, and amounts owed data for this
                        property requires a connection to your accounting system
                        (Entrata).
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                        To enable financial data, Accounts Receivable Codes
                        (getArCodes) must be granted in your Entrata portal.
                        Additionally, you should enable the Accounts Receivable
                        Transactions (getLeaseArTransactions) API in your
                        Entrata portal. Once configured, EZOS will be able to
                        sync transaction data including deposits, past due
                        balances, and amounts owed automatically.
                    </Typography>

                    <Box
                        style={{
                            backgroundColor: '#f8fafc',
                            borderRadius: 8,
                            padding: 16,
                        }}
                    >
                        <Typography
                            variant="body2"
                            style={{ fontWeight: 600, marginBottom: 8 }}
                        >
                            How to connect:
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            component="div"
                        >
                            <ol style={{ margin: 0, paddingLeft: 20 }}>
                                <li style={{ marginBottom: 4 }}>
                                    Log in to your Entrata portal
                                </li>
                                <li style={{ marginBottom: 4 }}>
                                    Go to Apps &gt; API Access
                                </li>
                                <li style={{ marginBottom: 4 }}>
                                    Search for EZ Turn
                                </li>
                                <li style={{ marginBottom: 4 }}>
                                    Provide access to the Accounts Receivable
                                    Codes (getArCodes) and Accounts Receivable
                                    Transactions (getLeaseArTransactions) APIs
                                </li>
                                <li>
                                    Return here and click &quot;Check
                                    Connection&quot; below
                                </li>
                            </ol>
                        </Typography>
                    </Box>

                    {connectionResult === 'success' && (
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{
                                gap: 8,
                                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                                borderRadius: 8,
                                padding: 12,
                            }}
                        >
                            <CheckCircleOutlineIcon
                                style={{ color: '#10b981', fontSize: 20 }}
                            />
                            <Typography
                                variant="body2"
                                style={{ color: '#065f46' }}
                            >
                                Connected successfully! Close this dialog and
                                click &quot;Sync Transactions&quot; to pull in
                                your financial data.
                            </Typography>
                        </Box>
                    )}

                    {connectionResult === 'failed' && (
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{
                                gap: 8,
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                borderRadius: 8,
                                padding: 12,
                            }}
                        >
                            <ErrorOutlineIcon
                                style={{ color: '#ef4444', fontSize: 20 }}
                            />
                            <Typography
                                variant="body2"
                                style={{ color: '#991b1b' }}
                            >
                                Still not connected. Please verify the API
                                settings in Entrata and try again.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions className={classes.configDialogActions}>
                <Button onClick={handleClose} disabled={checking}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCheckConnection}
                    disabled={checking}
                    disableElevation
                    startIcon={
                        checking ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <SyncIcon />
                        )
                    }
                >
                    {checking ? 'Checking...' : 'Check Connection'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
